<script lang="ts">
import { cloneDeep, sortBy } from 'lodash'
import {
  computed,
  ComputedRef,
  defineComponent,
  onMounted,
  PropType,
  Ref,
  ref,
  watch,
} from 'vue'

import { opChain } from '@collector/shared-utils'
import {
  AmericanFootballSackAttribute,
  DetailType,
  EventsLineupsParticipant,
  IncidentAttributeId,
  SubParticipantPosition,
} from '@collector/sportsapi-client-legacy'
import UpdateIncident from '@mobile/ActionQueue/Actions/Incidents/UpdateIncident'
import Button from '@mobile/components/Button/Button.vue'
import Select from '@mobile/components/Select/Select.vue'
import { getOppositeTeam, getTeamSide } from '@mobile/reusables/entityUtils'
import {
  DetailButtonsConfiguration,
  DetailConfiguration,
  DetailSelectConfiguration,
  enforceMinMax,
  getAttributeId,
  getPropertyIndex,
  isDetailsComponentConfiguration,
  ListOption,
  useSportOptions,
} from '@mobile/views/Relation/DetailsPanel/Sports/composables/useSportOptions'
import { getFilteredSelectOptions } from '@mobile/views/Relation/DetailsPanel/Table/partcipantsSearchFilter'
import { useLineupsSelectOptions } from '@mobile/views/Relation/Shared/Lineups/useLineupsSelectOption'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import { useSportConfiguration } from '@mobile/views/Relation/Shared/RelationSportCommonDependencies/useSportConfiguration'

import Modal from '../Modal.vue'
import ModalButtons from '../ModalButtons.vue'
import ModalTitle from '../ModalTitle.vue'
import IncidentBasicInfo from './IncidentBasicInfo.vue'

export default defineComponent({
  components: {
    Button,
    Modal,
    ModalTitle,
    ModalButtons,
    IncidentBasicInfo,
    Select,
  },
  props: {
    linkedIncident: {
      type: Object as PropType<ProbableLinkedIncident>,
      required: true,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { actionQueue, probableEvent, sport, event } = useEventInjections()
    const { id: event_id } = probableEvent.value
    const participantSearchValue: Ref<string> = ref('')
    const { detailsConfiguration } = useSportOptions(sport)
    const { assignableIncidentsConfiguration, eventClockConfiguration } =
      useSportConfiguration()
    const oppositeTeamSide = computed(() => {
      if (props.linkedIncident.participant) {
        return getTeamSide(
          event.value.data.participants,
          getOppositeTeam(
            event.value.data.participants,
            props.linkedIncident.participant,
          )?.id ?? 0,
        )
      }

      return undefined
    })
    const assignableIncidentConfiguration = props.linkedIncident.incident
      ?.incident_id
      ? assignableIncidentsConfiguration[
          props.linkedIncident.incident.incident_id
        ]
      : null
    const shouldLoadOppositeTeam =
      props.linkedIncident.incident.attribute_ids?.some(
        (attribute) =>
          !!assignableIncidentConfiguration?.attributesConfiguration?.[
            attribute
          ]?.loadOppositeTeamWhenSelected,
      )
    const teamSide = computed(() => props.linkedIncident.side || undefined)
    const { oppositeLineupsParticipants } = useLineupsSelectOptions(
      ref(props.linkedIncident.incident),
      shouldLoadOppositeTeam ? oppositeTeamSide.value : teamSide.value,
    )
    const selectedGoalkeeper = ref(
      props.linkedIncident.incident.additional_info?.goalkeeper_id,
    )
    const selectedAttributes = ref([
      ...props.linkedIncident.incident.attribute_ids,
    ])
    const incidentDetailsConfiguration = computed(
      () =>
        detailsConfiguration[props.linkedIncident.incident.incident_id] ?? null,
    ) as ComputedRef<DetailConfiguration<IncidentAttributeId>[] | null>
    const inputValues = ref(cloneDeep(props.linkedIncident.incident.properties))

    watch(props.linkedIncident.incident, (incident) => {
      inputValues.value = incident.properties
    })

    onMounted(() => {
      if (!incidentDetailsConfiguration.value) return

      if (isDetailsComponentConfiguration(incidentDetailsConfiguration.value))
        return

      const detailTypeInput = incidentDetailsConfiguration.value.find(
        (detail) => detail.type === DetailType.Input,
      )

      if (!inputValues.value?.length && detailTypeInput) {
        inputValues.value?.push({
          short_name: detailTypeInput?.name || '',
          value: null,
        })
      }
    })

    const time = computed(() => {
      const [minute, second] = props.linkedIncident.incident.event_time?.split(
        ':',
      ) ?? ['00', '00']

      return {
        minute,
        second,
      }
    })

    const filteredSelectOptions = computed(() =>
      getFilteredSelectOptions(
        sortParticipantsByGoalkeeper(oppositeLineupsParticipants.value),
        participantSearchValue.value,
      ),
    )

    function sortParticipantsByGoalkeeper(
      participants: EventsLineupsParticipant[],
    ): EventsLineupsParticipant[] {
      return sortBy(participants, [
        (participant) => participant.participant_name !== 'None',
        (participant) =>
          participant.participant_position !==
          SubParticipantPosition.Goalkeeper,
        (participant) => participant.participant_shirt_number,
      ])
    }

    function onParticipantSearchChange(value: string): void {
      participantSearchValue.value = value
    }

    function isSelectedAttribute(attributeId: number): boolean {
      return selectedAttributes.value.includes(attributeId)
    }

    function isAttributeDisabled(attributeId: IncidentAttributeId): boolean {
      if (!incidentDetailsConfiguration.value) return false

      const activeWith = incidentDetailsConfiguration.value
        .filter(
          (config) =>
            config.type === DetailType.Button &&
            config.activeWhen &&
            config.attributes.some(
              (_attributeId) => _attributeId === attributeId,
            ),
        )
        .map((x) => x.activeWhen && x.activeWhen(selectedAttributes.value))

      if (!activeWith || activeWith.length === 0) return false

      return activeWith.some((x) => x === false)
    }

    function clearAttributesGroup(
      config:
        | DetailButtonsConfiguration<IncidentAttributeId>
        | DetailSelectConfiguration<IncidentAttributeId>,
    ): void {
      config.attributes.forEach((attributeId) => {
        const attributeIndex = selectedAttributes.value.indexOf(
          getAttributeId(attributeId),
        )

        if (attributeIndex !== -1) {
          selectedAttributes.value.splice(attributeIndex, 1)
        }
      })
    }

    function addAttribute(
      linkedIncident: ProbableLinkedIncident,
      attributeId: number,
      config:
        | DetailButtonsConfiguration<IncidentAttributeId>
        | DetailSelectConfiguration<IncidentAttributeId>,
    ): void {
      if (config.multiple) {
        selectedAttributes.value = selectedAttributes.value.includes(
          attributeId,
        )
          ? selectedAttributes.value.filter(
              (element) => element !== attributeId,
            )
          : [...selectedAttributes.value, attributeId]
      } else {
        const index = selectedAttributes.value.indexOf(attributeId)

        if (index !== -1) {
          selectedAttributes.value.splice(index, 1)
        } else {
          clearAttributesGroup(config)
          selectedAttributes.value.push(attributeId)
        }
      }
    }

    function getSelectDetailGroupValue(
      options: ListOption[],
    ): number | undefined {
      return selectedAttributes.value.find((attributeId) =>
        options.some((option) => option.value === attributeId),
      )
    }

    function getAttributeName(attributeId: IncidentAttributeId): string {
      // TODO: remove when SportsAPI will add this attribute
      if (attributeId === AmericanFootballSackAttribute.NotAssisted) {
        return 'Not assisted'
      }

      return (
        props.linkedIncident.sportIncident.attributes.find(
          ({ id }) => id === attributeId,
        )?.name ?? 'N/A'
      )
    }

    function updateIncident(): void {
      actionQueue.value.add(
        new UpdateIncident(props.linkedIncident.incident.id, {
          incident_id: props.linkedIncident.incident.incident_id,
          event_id,
          status_id: props.linkedIncident.incident.event_status_id,
          attribute_ids: selectedAttributes.value,
          participant_team_id: props.linkedIncident.incident.participant_id,
          participant_id: props.linkedIncident.incident.subparticipant_id,
          participant_name: props.linkedIncident.incident.subparticipant_id
            ? props.linkedIncident.incident.subparticipant_name
            : '',
          assistant_id:
            props.linkedIncident.incident.additional_info?.assistant_id,
          assistant_name:
            props.linkedIncident.incident.additional_info?.assistant_name,
          secondary_assistant_id:
            props.linkedIncident.incident.additional_info
              ?.secondary_assistant_id,
          secondary_assistant_name:
            props.linkedIncident.incident.additional_info
              ?.secondary_assistant_name,
          goalkeeper_id: selectedGoalkeeper.value ?? undefined,
          properties: inputValues.value?.map((p) => ({
            short_name: p.short_name.toLowerCase(),
            value: p.value?.toLocaleString().length ? p.value : null,
          })),
          minute: Number(time.value.minute),
          second: Number(time.value.second),
        }),
      )
      emit('close')
    }

    return {
      getAttributeName,
      selectedAttributes,
      clearAttributesGroup,
      updateIncident,
      time,
      incidentDetailsConfiguration,
      isAttributeDisabled,
      addAttribute,
      isSelectedAttribute,
      selectedGoalkeeper,
      onParticipantSearchChange,
      filteredSelectOptions,
      DetailType,
      getPropertyIndex,
      inputValues,
      enforceMinMax,
      getSelectDetailGroupValue,
      opChain,
      getAttributeId,
      eventClockConfiguration,
    }
  },
})
</script>

<template>
  <Modal id="incidentDetailsModal">
    <ModalTitle title="Incident details" />
    <IncidentBasicInfo :linkedIncident />
    <div class="flex flex-col justify-between">
      <div
        v-for="(config, index) in incidentDetailsConfiguration"
        :key="index"
      >
        <div class="pb-2 text-center font-bold">
          {{ config.name }}
        </div>
        <div v-if="config.type === DetailType.Input">
          <input
            v-if="
              inputValues &&
              opChain(
                inputValues,
                (v) => v[getPropertyIndex(inputValues, config.name)],
              )
            "
            v-model="
              inputValues[getPropertyIndex(inputValues, config.name)].value
            "
            class="focus:shadow-outline w-full appearance-none rounded-sm border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-hidden"
            :placeholder="config.placeholder"
            :min="config.min"
            :max="config.max"
            @keyup="
              enforceMinMax(
                inputValues,
                linkedIncident.incident.properties,
                config,
              )
            "
          />
        </div>
        <div v-if="config.type === DetailType.Select">
          <Select
            :value="
              getSelectDetailGroupValue(config.optionsFactory(linkedIncident))
            "
            :options="[
              { name: 'Not selected', value: null },
              ...config.optionsFactory(linkedIncident),
            ]"
            label="name"
            :mode="config.multiple ? 'multiple' : 'single'"
            @update:value="
              $event
                ? addAttribute(linkedIncident, $event, config)
                : clearAttributesGroup(config)
            "
          />
        </div>
        <div
          v-if="config.type === DetailType.Button"
          :class="`grid gap-2 grid-cols-${config.cols} mb-3`"
        >
          <Button
            v-for="attributeOrAttributeConfig in config.attributes"
            :key="getAttributeId(attributeOrAttributeConfig)"
            :color="
              isSelectedAttribute(getAttributeId(attributeOrAttributeConfig))
                ? 'blue'
                : 'white'
            "
            :size="'lg'"
            :disabled="
              isAttributeDisabled(getAttributeId(attributeOrAttributeConfig))
            "
            @click="
              addAttribute(
                linkedIncident,
                getAttributeId(attributeOrAttributeConfig),
                config,
              )
            "
          >
            {{ getAttributeName(getAttributeId(attributeOrAttributeConfig)) }}
          </Button>
        </div>
        <div
          v-if="config.name.includes('Goalkeeper')"
          class="mb-3"
        >
          <Select
            v-model="selectedGoalkeeper"
            class="flex-1!"
            label="name"
            :mode="'single'"
            :options="filteredSelectOptions"
            searchable
            trackBy="name"
            @searchChange="onParticipantSearchChange"
          />
        </div>
      </div>
      <ModalButtons>
        <Button
          color="red"
          :size="'lg'"
          @click="$emit('close')"
        >
          Close
        </Button>
        <Button
          class="px-8"
          color="green"
          :size="'lg'"
          @click="updateIncident"
        >
          Save
        </Button>
      </ModalButtons>
    </div>
  </Modal>
</template>
